/**
 * Assets — 4-stage approval + full post-paid lifecycle:
 *   in_stock ↔ issued ↔ transfer / maintenance / retire / scrap
 *
 * Invoice attachment is MANDATORY at creation (backend enforced, frontend blocks
 * Submit until a file is uploaded).
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Plus, Package, Check, X, AlertCircle, CornerUpLeft, Pencil, Send,
  Wallet, History as HistoryIcon, Upload, ImageIcon, Wrench,
  ArrowRightLeft, LogIn, LogOut, Recycle, Sparkles, MapPin, QrCode,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";

/* ==================================================================
 *  Constants
 * =================================================================*/
const CATS = [
  { v: "furniture", l: "Furniture" },
  { v: "electronics", l: "Electronics" },
  { v: "kitchen", l: "Kitchen" },
  { v: "bedding", l: "Bedding" },
  { v: "safety", l: "Safety" },
  { v: "cleaning", l: "Cleaning" },
  { v: "office", l: "Office" },
  { v: "misc", l: "Miscellaneous" },
];

const STATUS_LABEL = {
  pending_manager: "Awaiting Hostel Manager",
  pending_director: "Awaiting Director",
  pending_finance: "Awaiting Finance",
  paid: "Paid",
  rejected: "Rejected",
  correction_needed: "Correction Required",
};

const STAGE_ROLE = {
  pending_manager: "hostel_manager",
  pending_director: "director",
  pending_finance: "accountant",
};

const SEND_BACK_REASONS = [
  { v: "wrong_amount", l: "Wrong Amount" },
  { v: "wrong_vendor", l: "Wrong Vendor" },
  { v: "wrong_category", l: "Wrong Category" },
  { v: "missing_invoice", l: "Missing Invoice" },
  { v: "duplicate_request", l: "Duplicate Request" },
  { v: "budget_exceeded", l: "Budget Exceeded" },
  { v: "incorrect_asset_details", l: "Incorrect Asset Details" },
  { v: "other", l: "Other" },
];

const LIFECYCLE_LABEL = {
  in_stock: "In Stock",
  issued: "Issued",
  in_maintenance: "In Maintenance",
  scrapped: "Scrapped",
  retired: "Retired",
};

const LIFECYCLE_TRANSITIONS = {
  in_stock:       ["issue", "transfer", "maintenance", "retire", "scrap"],
  issued:         ["return", "transfer", "maintenance", "retire", "scrap"],
  in_maintenance: ["close_maintenance", "retire", "scrap"],
  scrapped:       [],
  retired:        [],
};

const CONDITIONS = [
  { v: "new", l: "New" }, { v: "good", l: "Good" }, { v: "fair", l: "Fair" },
  { v: "poor", l: "Poor" }, { v: "damaged", l: "Damaged" },
];

const empty = {
  name: "", category: "furniture", vendor: "", amount: "", quantity: 1,
  description: "", serial_no: "", location: "",
  purchase_date: new Date().toISOString().slice(0, 10),
  warranty_end: "", invoice_url: "",
};

async function fileToBase64(file, maxSide = 1600) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("File read failed"));
    reader.onload = () => {
      if (!file.type.startsWith("image/")) return resolve({ dataUrl: reader.result });
      const img = new Image();
      img.onload = () => {
        const scale = Math.min(1, maxSide / Math.max(img.width, img.height));
        const w = Math.round(img.width * scale);
        const h = Math.round(img.height * scale);
        const c = document.createElement("canvas"); c.width = w; c.height = h;
        const ctx = c.getContext("2d"); ctx.fillStyle = "#fff"; ctx.fillRect(0, 0, w, h);
        ctx.drawImage(img, 0, 0, w, h);
        resolve({ dataUrl: c.toDataURL("image/jpeg", 0.85) });
      };
      img.onerror = () => reject(new Error("Not a valid image"));
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  });
}

/* ==================================================================
 *  Main page
 * =================================================================*/
export default function Assets() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [invoicePreview, setInvoicePreview] = useState(null);
  const [detail, setDetail] = useState(null);
  const [filter, setFilter] = useState("all");
  const [remarks, setRemarks] = useState("");

  // approval-side dialogs
  const [sendBackOpen, setSendBackOpen] = useState(false);
  const [sendBackReason, setSendBackReason] = useState("");
  const [sendBackRemarks, setSendBackRemarks] = useState("");
  const [editOpen, setEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(empty);
  const [editInvoicePreview, setEditInvoicePreview] = useState(null);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ utr: "", mode: "bank_transfer", reference: "", remarks: "" });

  // lifecycle dialog
  const [lcAction, setLcAction] = useState(null);  // 'issue' | 'return' | 'transfer' | 'maintenance' | 'close_maintenance' | 'retire' | 'scrap'
  const [lcForm, setLcForm] = useState({ target_type: "room", target_id: "", condition: "", remarks: "" });
  const [locations, setLocations] = useState({ rooms: [], floors: [], residents: [] });

  const load = () => api.get("/assets").then((r) => setList(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Auto-open detail when arriving with ?open=<asset_id> (from QR sticker scan)
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    const openId = p.get("open");
    if (!openId || list.length === 0) return;
    const found = list.find((x) => x.id === openId);
    if (found) setDetail(found);
  }, [list]);

  const downloadQr = async (asset) => {
    try {
      const res = await fetch(`${window.location.origin.replace(window.location.pathname, "")}/api/assets/${asset.id}/qr`, {
        credentials: "include",
      });
      if (!res.ok) throw new Error("QR download failed");
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `asset-${asset.name.replace(/\s+/g, "_")}-qr.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("QR sticker downloaded");
    } catch (err) { toast.error(err.message || "QR download failed"); }
  };

  useEffect(() => {
    // Load holders lazily when the lifecycle dialog opens
    if (!lcAction) return;
    Promise.all([
      api.get("/rooms"), api.get("/floors"), api.get("/residents"),
    ]).then(([r, f, res]) => setLocations({
      rooms: r.data, floors: f.data, residents: res.data,
    })).catch(() => {});
  }, [lcAction]);

  /* ---- Invoice upload (mandatory) ---- */
  const handleInvoice = async (file, target = "create") => {
    if (!file) return;
    try {
      const { dataUrl } = await fileToBase64(file);
      if (target === "edit") {
        setEditInvoicePreview(dataUrl);
        setEditForm((f) => ({ ...f, invoice_url: dataUrl }));
      } else {
        setInvoicePreview(dataUrl);
        setForm((f) => ({ ...f, invoice_url: dataUrl }));
      }
    } catch (e) { toast.error(e.message || "Could not read file"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.invoice_url) { toast.error("Invoice is mandatory — upload the vendor invoice first."); return; }
    try {
      await api.post("/assets", { ...form,
        amount: parseFloat(form.amount),
        quantity: parseInt(form.quantity) || 1 });
      toast.success("Asset submitted for approval");
      setOpen(false); setForm(empty); setInvoicePreview(null); load();
    } catch (err) { toast.error(formatApiError(err)); }
  };

  const refreshDetail = async (id) => {
    try { const { data } = await api.get("/assets"); setList(data);
          setDetail(data.find((x) => x.id === id) || null); }
    catch (err) { console.error("refreshDetail failed", err); }
  };

  /* ---- RBAC helpers ---- */
  const canActOn = (a) => {
    if (!user || !a) return false;
    if (user.role === "admin") return a.status in STAGE_ROLE;
    return STAGE_ROLE[a.status] === user.role;
  };
  const isMaker = (a) => a && (a.created_by === user?.id || user?.role === "admin" || user?.role === "hostel_manager");
  const canEdit = (a) => isMaker(a) && a.status === "correction_needed";
  const canPay  = (a) => (user?.role === "accountant" || user?.role === "admin") && a.status === "pending_finance";
  const canLifecycle = (a) => a?.status === "paid"
    && ["admin", "warden", "hostel_manager"].includes(user?.role);

  /* ---- Approval actions ---- */
  const doApprove = async () => {
    try { await api.post(`/assets/${detail.id}/approve`, { remarks });
      toast.success("Approved"); setRemarks(""); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doReject = async () => {
    try { await api.post(`/assets/${detail.id}/reject`, { remarks });
      toast.success("Rejected"); setRemarks(""); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doSendBack = async () => {
    if (!sendBackReason) { toast.error("Pick a reason"); return; }
    if (!sendBackRemarks.trim()) { toast.error("Remarks are mandatory"); return; }
    try {
      await api.post(`/assets/${detail.id}/send-back`,
        { reason: sendBackReason, remarks: sendBackRemarks });
      toast.success("Sent back for correction");
      setSendBackOpen(false); setSendBackReason(""); setSendBackRemarks("");
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doResubmit = async () => {
    try { await api.post(`/assets/${detail.id}/resubmit`, { remarks });
      toast.success("Resubmitted"); setRemarks(""); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };
  const doPay = async () => {
    try { await api.post(`/assets/${detail.id}/payment`, payForm);
      toast.success("Payment recorded — asset is In Stock");
      setPayOpen(false); setPayForm({ utr: "", mode: "bank_transfer", reference: "", remarks: "" });
      await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---- Edit ---- */
  const openEdit = () => {
    setEditForm({
      name: detail.name, category: detail.category, vendor: detail.vendor || "",
      amount: String(detail.amount ?? ""), quantity: detail.quantity ?? 1,
      description: detail.description || "", serial_no: detail.serial_no || "",
      location: detail.location || "", purchase_date: detail.purchase_date || "",
      warranty_end: detail.warranty_end || "", invoice_url: detail.invoice_url || "",
    });
    setEditInvoicePreview(detail.invoice_url || null);
    setEditOpen(true);
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editForm.invoice_url) { toast.error("Invoice is mandatory."); return; }
    try {
      await api.put(`/assets/${detail.id}`, { ...editForm,
        amount: parseFloat(editForm.amount),
        quantity: parseInt(editForm.quantity) || 1 });
      toast.success("Saved (new version)");
      setEditOpen(false); await refreshDetail(detail.id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---- Lifecycle actions ---- */
  const openLifecycle = (action) => {
    setLcAction(action);
    setLcForm({ target_type: "room", target_id: "", condition: "", remarks: "" });
  };
  const doLifecycle = async () => {
    if (!lcAction || !detail) return;
    const id = detail.id;
    try {
      let payload = { remarks: lcForm.remarks };
      let path = "";
      if (lcAction === "issue")    { path = "issue";    payload = { ...payload, target_type: lcForm.target_type, target_id: lcForm.target_id, condition: lcForm.condition || undefined }; }
      else if (lcAction === "transfer") { path = "transfer"; payload = { ...payload, target_type: lcForm.target_type, target_id: lcForm.target_id, condition: lcForm.condition || undefined }; }
      else if (lcAction === "return")   { path = "return";   payload = { ...payload, condition: lcForm.condition || undefined }; }
      else if (lcAction === "maintenance") { path = "maintenance"; payload = { remarks: lcForm.remarks || "-" }; }
      else if (lcAction === "close_maintenance") { path = "maintenance/close"; payload = { ...payload, condition: lcForm.condition || undefined }; }
      else if (lcAction === "retire") { path = "retire"; payload = { kind: "retire", remarks: lcForm.remarks || "-" }; }
      else if (lcAction === "scrap")  { path = "retire"; payload = { kind: "scrap",  remarks: lcForm.remarks || "-" }; }
      // Basic validation
      if (["issue", "transfer"].includes(lcAction) && !lcForm.target_id) {
        toast.error("Pick a target"); return;
      }
      if (["maintenance", "retire", "scrap"].includes(lcAction) && !lcForm.remarks?.trim()) {
        toast.error("Remarks are mandatory"); return;
      }
      await api.post(`/assets/${id}/${path}`, payload);
      toast.success(`Lifecycle updated: ${lcAction.replace("_", " ")}`);
      setLcAction(null); await refreshDetail(id);
    } catch (err) { toast.error(formatApiError(err)); }
  };

  /* ---- Derived ---- */
  const filtered = useMemo(() => list.filter((a) => {
    if (filter === "all") return true;
    if (filter === "pending") return a.status.startsWith("pending");
    if (filter === "correction") return a.status === "correction_needed";
    if (filter === "in_stock") return a.status === "paid" && a.lifecycle_status === "in_stock";
    if (filter === "issued")   return a.status === "paid" && a.lifecycle_status === "issued";
    if (filter === "maintenance") return a.status === "paid" && a.lifecycle_status === "in_maintenance";
    if (filter === "retired")  return a.status === "paid" && ["retired","scrapped"].includes(a.lifecycle_status);
    return a.status === filter;
  }), [list, filter]);

  const badgeVariant = (s) =>
    s === "paid" ? "default" : s === "rejected" || s === "correction_needed" ? "destructive" : "secondary";
  const lifecycleColor = (s) => ({
    in_stock: "bg-emerald-100 text-emerald-800",
    issued: "bg-primary/10 text-primary",
    in_maintenance: "bg-amber-100 text-amber-800",
    scrapped: "bg-rose-100 text-rose-800",
    retired: "bg-slate-200 text-slate-700",
  }[s] || "bg-secondary");

  /* ==================================================================
   *  RENDER
   * =================================================================*/
  return (
    <div className="space-y-6" data-testid="assets-page">
      <div className="flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="eyebrow text-muted-foreground">Procurement · Inventory</div>
          <h1 className="font-display text-4xl font-bold mt-1">Assets</h1>
          <p className="text-muted-foreground mt-1 max-w-2xl">
            4-stage approval (Warden → Manager → Director → Finance). Once paid, track lifecycle
            through Issue · Return · Transfer · Maintenance · Retire — invoice mandatory at creation.
          </p>
        </div>
        <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setForm(empty); setInvoicePreview(null); } }}>
          <DialogTrigger asChild>
            <Button data-testid="add-asset-button"><Plus className="w-4 h-4 mr-2" /> New Asset</Button>
          </DialogTrigger>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Log Asset</DialogTitle></DialogHeader>
            <form onSubmit={submit} className="space-y-4">
              <InvoiceUpload preview={invoicePreview}
                onFile={(f) => handleInvoice(f, "create")}
                onClear={() => { setInvoicePreview(null); setForm((x)=>({...x, invoice_url:""})); }}
                testId="asset-invoice" />
              <AssetFormFields form={form} setForm={setForm} testPrefix="asset" />
              <DialogFooter>
                <Button type="submit" disabled={!form.invoice_url} data-testid="asset-save">
                  Submit for Approval
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList data-testid="asset-filter-tabs" className="flex-wrap h-auto">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="correction">Correction</TabsTrigger>
          <TabsTrigger value="in_stock">In Stock</TabsTrigger>
          <TabsTrigger value="issued">Issued</TabsTrigger>
          <TabsTrigger value="maintenance">Maintenance</TabsTrigger>
          <TabsTrigger value="retired">Retired / Scrapped</TabsTrigger>
          <TabsTrigger value="rejected">Rejected</TabsTrigger>
        </TabsList>
      </Tabs>

      <Card className="p-0 border-border bg-white overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-muted-foreground border-b border-border">
                <th className="py-3 px-4">Asset</th>
                <th className="py-3 pr-4">Category</th>
                <th className="py-3 pr-4">Vendor</th>
                <th className="py-3 pr-4">Qty</th>
                <th className="py-3 pr-4">Current Holder</th>
                <th className="py-3 pr-4 text-right">Amount</th>
                <th className="py-3 pr-4">Approval</th>
                <th className="py-3 pr-4">Lifecycle</th>
                <th className="py-3 pr-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((a) => (
                <tr key={a.id} className="border-b border-border/50 hover:bg-secondary/40">
                  <td className="py-3 px-4 font-medium">{a.name}</td>
                  <td className="py-3 pr-4 capitalize">{a.category}</td>
                  <td className="py-3 pr-4 text-muted-foreground">{a.vendor || "—"}</td>
                  <td className="py-3 pr-4 font-mono text-xs">{a.quantity}</td>
                  <td className="py-3 pr-4 text-xs">
                    {a.assigned_to ? (
                      <span className="text-primary font-medium">
                        <MapPin className="w-3 h-3 inline mr-1" />
                        {a.assigned_to.type}: {a.assigned_to.label}
                      </span>
                    ) : <span className="text-muted-foreground">—</span>}
                  </td>
                  <td className="py-3 pr-4 text-right font-mono">₹{Number(a.amount).toLocaleString("en-IN")}</td>
                  <td className="py-3 pr-4">
                    <Badge variant={badgeVariant(a.status)} className="text-xs">
                      {STATUS_LABEL[a.status] || a.status}
                    </Badge>
                    {a.version > 1 && <span className="ml-2 text-[10px] text-muted-foreground">v{a.version}</span>}
                  </td>
                  <td className="py-3 pr-4">
                    {a.lifecycle_status ? (
                      <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${lifecycleColor(a.lifecycle_status)}`}
                        data-testid={`lifecycle-${a.id}`}>
                        {LIFECYCLE_LABEL[a.lifecycle_status] || a.lifecycle_status}
                      </span>
                    ) : <span className="text-muted-foreground text-xs">—</span>}
                  </td>
                  <td className="py-3 pr-4 text-right">
                    <Button size="sm" variant="outline" onClick={() => setDetail(a)}
                      data-testid={`open-asset-${a.id}`}>Open</Button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={9} className="py-10 text-center text-muted-foreground">
                  <Package className="w-8 h-8 mx-auto mb-2 opacity-40" /> No assets in this bucket.
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Detail dialog */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl flex items-center gap-3 flex-wrap">
                  {detail.name} — ₹{Number(detail.amount).toLocaleString("en-IN")}
                  <Badge variant={badgeVariant(detail.status)}>{STATUS_LABEL[detail.status] || detail.status}</Badge>
                  {detail.lifecycle_status && (
                    <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${lifecycleColor(detail.lifecycle_status)}`}>
                      {LIFECYCLE_LABEL[detail.lifecycle_status]}
                    </span>
                  )}
                  {detail.version > 1 && <Badge variant="outline" className="text-[10px]">v{detail.version}</Badge>}
                </DialogTitle>
                {detail.status === "correction_needed" && detail.correction_reason && (
                  <DialogDescription className="text-destructive">
                    <AlertCircle className="w-3.5 h-3.5 inline mr-1" />
                    Correction requested — reason: <b>{SEND_BACK_REASONS.find((r)=>r.v===detail.correction_reason)?.l || detail.correction_reason}</b>
                  </DialogDescription>
                )}
                {detail.assigned_to && (
                  <DialogDescription>
                    <MapPin className="w-3.5 h-3.5 inline mr-1 text-primary" />
                    Currently with <b>{detail.assigned_to.type}: {detail.assigned_to.label}</b>
                    {detail.condition && <> · Condition: <b className="capitalize">{detail.condition}</b></>}
                  </DialogDescription>
                )}
              </DialogHeader>

              <Tabs defaultValue="detail" className="mt-2">
                <TabsList>
                  <TabsTrigger value="detail" data-testid="tab-detail">Details</TabsTrigger>
                  <TabsTrigger value="lifecycle" data-testid="tab-lifecycle">
                    <Sparkles className="w-3.5 h-3.5 mr-1.5" /> Lifecycle ({(detail.lifecycle_history || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="history" data-testid="tab-history">
                    <HistoryIcon className="w-3.5 h-3.5 mr-1.5" /> Approvals ({(detail.approval_history || []).length})
                  </TabsTrigger>
                  <TabsTrigger value="versions" data-testid="tab-versions">Versions ({(detail.versions || []).length})</TabsTrigger>
                </TabsList>

                {/* --- DETAIL --- */}
                <TabsContent value="detail" className="pt-2 space-y-3">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <Info label="Category" value={detail.category} />
                    <Info label="Vendor" value={detail.vendor || "—"} />
                    <Info label="Quantity" value={detail.quantity} />
                    <Info label="Serial No" value={detail.serial_no || "—"} />
                    <Info label="Location" value={detail.location || "—"} />
                    <Info label="Purchase Date" value={detail.purchase_date || "—"} />
                    <Info label="Warranty End" value={detail.warranty_end || "—"} />
                    <Info label="Created By" value={detail.created_by_name} />
                  </div>
                  {detail.description && (
                    <div><div className="eyebrow text-muted-foreground mb-1">Description</div>
                      <div className="text-sm">{detail.description}</div></div>
                  )}
                  {detail.invoice_url && (
                    <div>
                      <div className="eyebrow text-muted-foreground mb-1">Invoice</div>
                      {detail.invoice_url.startsWith("data:image") ? (
                        <img src={detail.invoice_url} alt="Invoice"
                          className="max-h-96 rounded-md border border-border" />
                      ) : detail.invoice_url.startsWith("data:") ? (
                        <a href={detail.invoice_url} download="invoice"
                          className="text-sm text-primary underline">Download invoice →</a>
                      ) : (
                        <a href={detail.invoice_url} target="_blank" rel="noreferrer"
                          className="text-sm text-primary underline">Open invoice →</a>
                      )}
                    </div>
                  )}
                  {detail.payment && (
                    <Card className="p-3 border-primary/40 bg-primary/5">
                      <div className="text-xs font-semibold uppercase text-primary mb-2 flex items-center gap-2">
                        <Wallet className="w-4 h-4" /> Payment
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <div><span className="text-muted-foreground">UTR:</span> {detail.payment.utr || "—"}</div>
                        <div><span className="text-muted-foreground">Mode:</span> {detail.payment.mode}</div>
                        <div><span className="text-muted-foreground">Date:</span> {new Date(detail.payment.paid_at).toLocaleString()}</div>
                        <div><span className="text-muted-foreground">Ref:</span> {detail.payment.reference || "—"}</div>
                      </div>
                    </Card>
                  )}
                </TabsContent>

                {/* --- LIFECYCLE --- */}
                <TabsContent value="lifecycle" className="pt-2 space-y-2">
                  {detail.status !== "paid" && (
                    <div className="text-xs text-muted-foreground italic">
                      Lifecycle tracking activates once payment is recorded.
                    </div>
                  )}
                  {(detail.lifecycle_history || []).length === 0 && detail.status === "paid" && (
                    <div className="text-xs text-muted-foreground italic">No lifecycle events yet.</div>
                  )}
                  {(detail.lifecycle_history || []).map((h, i) => (
                    <div key={h.at || `lifecycle-${i}`} className="text-xs border-l-2 border-primary/40 pl-3 py-1.5"
                      data-testid={`lifecycle-entry-${i}`}>
                      <div className="flex items-center gap-2 flex-wrap">
                        <LifecycleBadge action={h.action} />
                        <span className="text-muted-foreground ml-auto">{new Date(h.at).toLocaleString()}</span>
                      </div>
                      <div className="mt-1">
                        <span className="font-medium">{h.by_name}</span>
                        <span className="text-muted-foreground"> ({h.by_role})</span>
                        {h.new?.assigned_to?.label && (
                          <span className="text-primary"> → {h.new.assigned_to.type}: {h.new.assigned_to.label}</span>
                        )}
                        {h.new?.condition && (
                          <span className="ml-2 text-muted-foreground">condition: {h.new.condition}</span>
                        )}
                        {h.remarks && <div className="text-muted-foreground mt-0.5">{h.remarks}</div>}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* --- APPROVAL HISTORY --- */}
                <TabsContent value="history" className="pt-2 space-y-2">
                  {(detail.approval_history || []).map((h, i) => (
                    <div key={h.at || `history-${i}`} className="text-xs border-l-2 border-primary/40 pl-3 py-1.5">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[10px] font-mono uppercase px-2 py-0.5 rounded bg-secondary">{h.action}</span>
                        <span className="text-muted-foreground">{h.step?.replace("pending_", "") || "—"}</span>
                        {h.version && <Badge variant="outline" className="text-[10px]">v{h.version}</Badge>}
                        <span className="text-muted-foreground ml-auto">{new Date(h.at).toLocaleString()}</span>
                      </div>
                      <div className="mt-1">
                        <span className="font-medium">{h.by_name}</span>
                        <span className="text-muted-foreground"> ({h.by_role})</span>
                        {h.reason && <span className="ml-2 text-destructive">· {SEND_BACK_REASONS.find((r)=>r.v===h.reason)?.l || h.reason}</span>}
                        {h.remarks && <div className="text-muted-foreground mt-0.5">{h.remarks}</div>}
                      </div>
                    </div>
                  ))}
                </TabsContent>

                {/* --- VERSIONS --- */}
                <TabsContent value="versions" className="pt-2 space-y-2">
                  {(detail.versions || []).map((v, i) => (
                    <div key={v.version || `version-${i}`} className="text-xs border border-border rounded-md p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <Badge variant="outline">v{v.version}</Badge>
                        <span className="font-medium">{v.by_name}</span>
                        <span className="text-muted-foreground">({v.by_role})</span>
                        <span className="text-muted-foreground ml-auto">{new Date(v.at).toLocaleString()}</span>
                      </div>
                      {v.changes && v.changes.length > 0 ? (
                        <div className="space-y-1">
                          {v.changes.map((c, j) => (
                            <div key={c.field || `change-${j}`} className="grid grid-cols-3 gap-2 font-mono text-[11px]">
                              <div className="text-muted-foreground">{c.field}</div>
                              <div className="text-destructive line-through truncate">{String(c.old ?? "—")}</div>
                              <div className="text-primary truncate">{String(c.new ?? "—")}</div>
                            </div>
                          ))}
                        </div>
                      ) : <div className="text-muted-foreground italic text-[11px]">Initial submission</div>}
                    </div>
                  ))}
                </TabsContent>
              </Tabs>

              {/* --- ACTIONS --- */}
              <div className="space-y-3 pt-4 mt-2 border-t border-border">
                {/* Approval-stage actions */}
                {canActOn(detail) && (
                  <div className="space-y-2">
                    <Textarea placeholder="Remarks (optional)" value={remarks} onChange={(e)=>setRemarks(e.target.value)}
                      className="min-h-[70px]" data-testid="asset-remarks" />
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={doApprove} data-testid="asset-approve">
                        <Check className="w-4 h-4 mr-2" /> Approve
                      </Button>
                      <Button variant="outline" onClick={() => setSendBackOpen(true)} data-testid="asset-send-back">
                        <CornerUpLeft className="w-4 h-4 mr-2" /> Send Back
                      </Button>
                      <Button variant="destructive" onClick={doReject} data-testid="asset-reject">
                        <X className="w-4 h-4 mr-2" /> Reject
                      </Button>
                      {canPay(detail) && (
                        <Button variant="secondary" onClick={() => setPayOpen(true)} data-testid="asset-pay">
                          <Wallet className="w-4 h-4 mr-2" /> Record Payment
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                {/* Maker-only edit on correction */}
                {canEdit(detail) && (
                  <div className="space-y-2 pt-2 border-t border-dashed border-border">
                    <div className="text-xs text-destructive flex items-start gap-2">
                      <AlertCircle className="w-3.5 h-3.5 mt-0.5" />
                      Sent back — edit and resubmit. Workflow will restart at Hostel Manager.
                    </div>
                    <div className="flex gap-2">
                      <Button onClick={openEdit} data-testid="asset-edit"><Pencil className="w-4 h-4 mr-2" /> Edit</Button>
                      <Button variant="secondary" onClick={doResubmit} data-testid="asset-resubmit">
                        <Send className="w-4 h-4 mr-2" /> Resubmit
                      </Button>
                    </div>
                  </div>
                )}
                {/* Lifecycle actions */}
                {canLifecycle(detail) && (
                  <div className="space-y-2 pt-2 border-t border-dashed border-border">
                    <div className="flex items-center justify-between">
                      <div className="eyebrow text-muted-foreground">Lifecycle actions</div>
                      <Button size="sm" variant="outline" onClick={() => downloadQr(detail)}
                        data-testid="asset-qr-download">
                        <QrCode className="w-3.5 h-3.5 mr-1.5" /> QR Sticker
                      </Button>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      {(LIFECYCLE_TRANSITIONS[detail.lifecycle_status] || []).map((act) => (
                        <LifecycleButton key={act} action={act} onClick={() => openLifecycle(act)} />
                      ))}
                      {(!LIFECYCLE_TRANSITIONS[detail.lifecycle_status]?.length) && (
                        <div className="text-xs italic text-muted-foreground">
                          Terminal state — no further lifecycle actions available.
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Send Back dialog */}
      <Dialog open={sendBackOpen} onOpenChange={setSendBackOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Send Back for Correction</DialogTitle>
            <DialogDescription>Pick a reason and add mandatory remarks.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>Reason *</Label>
              <Select value={sendBackReason} onValueChange={setSendBackReason}>
                <SelectTrigger data-testid="asset-sb-reason"><SelectValue placeholder="Choose a reason" /></SelectTrigger>
                <SelectContent>{SEND_BACK_REASONS.map((r)=><SelectItem key={r.v} value={r.v}>{r.l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Remarks *</Label>
              <Textarea required value={sendBackRemarks} onChange={(e)=>setSendBackRemarks(e.target.value)}
                data-testid="asset-sb-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setSendBackOpen(false)}>Cancel</Button>
            <Button onClick={doSendBack} data-testid="asset-sb-confirm">
              <CornerUpLeft className="w-4 h-4 mr-2" /> Send Back
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onOpenChange={setEditOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Asset</DialogTitle>
            <DialogDescription>Every save creates a new version — nothing is overwritten.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-4">
            <InvoiceUpload preview={editInvoicePreview}
              onFile={(f) => handleInvoice(f, "edit")}
              onClear={() => { setEditInvoicePreview(null); setEditForm((x)=>({...x, invoice_url:""})); }}
              testId="edit-asset-invoice" />
            <AssetFormFields form={editForm} setForm={setEditForm} testPrefix="edit-asset" />
            <DialogFooter>
              <Button type="button" variant="outline" onClick={()=>setEditOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={!editForm.invoice_url} data-testid="edit-asset-save">
                Save New Version
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Payment dialog */}
      <Dialog open={payOpen} onOpenChange={setPayOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Payment</DialogTitle>
            <DialogDescription>UTR / reference and payment mode. On save, asset becomes In Stock.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-2">
              <Label>UTR / Transaction No.</Label>
              <Input value={payForm.utr} onChange={(e)=>setPayForm({...payForm, utr:e.target.value})} data-testid="asset-pay-utr" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Mode</Label>
                <Select value={payForm.mode} onValueChange={(v)=>setPayForm({...payForm, mode:v})}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    <SelectItem value="upi">UPI</SelectItem>
                    <SelectItem value="cheque">Cheque</SelectItem>
                    <SelectItem value="card">Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Reference</Label>
                <Input value={payForm.reference} onChange={(e)=>setPayForm({...payForm, reference:e.target.value})} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Remarks</Label>
              <Textarea value={payForm.remarks} onChange={(e)=>setPayForm({...payForm, remarks:e.target.value})} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setPayOpen(false)}>Cancel</Button>
            <Button onClick={doPay} data-testid="asset-pay-confirm">
              <Wallet className="w-4 h-4 mr-2" /> Mark Paid
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Lifecycle action dialog */}
      <Dialog open={!!lcAction} onOpenChange={(v) => !v && setLcAction(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="capitalize flex items-center gap-2">
              {lcAction && <LifecycleBadge action={lcAction} />}
              <span>{(lcAction || "").replace("_", " ")}</span>
            </DialogTitle>
            <DialogDescription>Every action is captured in the lifecycle audit trail.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {(lcAction === "issue" || lcAction === "transfer") && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label>Assign to *</Label>
                    <Select value={lcForm.target_type}
                      onValueChange={(v)=>setLcForm({...lcForm, target_type: v, target_id: ""})}>
                      <SelectTrigger data-testid="lc-target-type"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="room">Room</SelectItem>
                        <SelectItem value="floor">Floor</SelectItem>
                        <SelectItem value="resident">Resident</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>{lcForm.target_type === "resident" ? "Resident" : lcForm.target_type === "floor" ? "Floor" : "Room"} *</Label>
                    <Select value={lcForm.target_id} onValueChange={(v)=>setLcForm({...lcForm, target_id: v})}>
                      <SelectTrigger data-testid="lc-target"><SelectValue placeholder="Pick target" /></SelectTrigger>
                      <SelectContent className="max-h-72">
                        {(lcForm.target_type === "room"
                          ? locations.rooms.map((r) => ({id: r.id, label: `Room ${r.room_no}`}))
                          : lcForm.target_type === "floor"
                          ? locations.floors.map((f) => ({id: f.id, label: `${f.name} (F${f.floor_number})`}))
                          : locations.residents.map((r) => ({id: r.id, label: `${r.name}${r.room_no ? ` · Room ${r.room_no}` : ""}`}))
                        ).map((o) => (
                          <SelectItem key={o.id} value={o.id}>{o.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <ConditionPicker value={lcForm.condition} onChange={(v)=>setLcForm({...lcForm, condition:v})} />
              </>
            )}
            {(lcAction === "return" || lcAction === "close_maintenance") && (
              <ConditionPicker value={lcForm.condition} onChange={(v)=>setLcForm({...lcForm, condition:v})} />
            )}
            <div className="space-y-2">
              <Label>
                Remarks {["maintenance","retire","scrap"].includes(lcAction) ? "*" : ""}
              </Label>
              <Textarea value={lcForm.remarks}
                onChange={(e)=>setLcForm({...lcForm, remarks:e.target.value})}
                placeholder={
                  lcAction === "maintenance" ? "Describe the issue / servicing task" :
                  lcAction === "retire" ? "Reason for retiring the asset" :
                  lcAction === "scrap" ? "Reason for scrapping the asset" :
                  "Optional notes"}
                data-testid="lc-remarks" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={()=>setLcAction(null)}>Cancel</Button>
            <Button onClick={doLifecycle} data-testid="lc-confirm">Confirm</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ==================================================================
 *  Sub-components
 * =================================================================*/
function InvoiceUpload({ preview, onFile, onClear, testId }) {
  const ref = useRef(null);
  return (
    <div className="space-y-2">
      <Label className="flex items-center gap-2">
        Invoice / Bill <span className="text-destructive">*</span>
        <span className="text-[10px] text-muted-foreground font-normal">(mandatory)</span>
      </Label>
      {preview ? (
        <div className="relative border border-border rounded-md overflow-hidden bg-secondary/30"
             data-testid={`${testId}-preview`}>
          {preview.startsWith("data:image") ? (
            <img src={preview} alt="Invoice" className="max-h-64 mx-auto" />
          ) : (
            <div className="p-6 flex items-center gap-3">
              <ImageIcon className="w-6 h-6 text-muted-foreground" />
              <span className="text-sm">Invoice file attached</span>
            </div>
          )}
          <button type="button" onClick={onClear}
            className="absolute top-2 right-2 w-8 h-8 rounded-full bg-white border border-border flex items-center justify-center hover:bg-destructive hover:text-destructive-foreground"
            data-testid={`${testId}-clear`}>
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); const f = e.dataTransfer.files?.[0]; if (f) onFile(f); }}
          onClick={() => ref.current?.click()}
          data-testid={`${testId}-dropzone`}
          className="border-2 border-dashed border-destructive/40 rounded-md p-8 text-center cursor-pointer hover:border-primary hover:bg-secondary/40 transition-colors"
        >
          <Upload className="w-6 h-6 text-muted-foreground mx-auto mb-2" />
          <div className="text-sm font-medium">Drop invoice or click to upload — required</div>
          <div className="text-xs text-muted-foreground">PDF or image accepted</div>
          <input ref={ref} type="file" accept="image/*,.pdf" className="hidden"
            onChange={(e) => onFile(e.target.files?.[0])} />
        </div>
      )}
    </div>
  );
}

function AssetFormFields({ form, setForm, testPrefix }) {
  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <div className="space-y-2 md:col-span-2">
          <Label>Name *</Label>
          <Input required value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}
            data-testid={`${testPrefix}-name`} />
        </div>
        <div className="space-y-2">
          <Label>Category *</Label>
          <Select value={form.category} onValueChange={(v)=>setForm({...form, category:v})}>
            <SelectTrigger data-testid={`${testPrefix}-category`}><SelectValue /></SelectTrigger>
            <SelectContent>{CATS.map((c)=><SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Vendor</Label>
          <Input value={form.vendor} onChange={(e)=>setForm({...form, vendor:e.target.value})}
            data-testid={`${testPrefix}-vendor`} />
        </div>
        <div className="space-y-2">
          <Label>Amount *</Label>
          <Input required type="number" step="0.01" value={form.amount}
            onChange={(e)=>setForm({...form, amount:e.target.value})} data-testid={`${testPrefix}-amount`} />
        </div>
        <div className="space-y-2">
          <Label>Quantity *</Label>
          <Input required type="number" min={1} value={form.quantity}
            onChange={(e)=>setForm({...form, quantity:e.target.value})} data-testid={`${testPrefix}-quantity`} />
        </div>
        <div className="space-y-2">
          <Label>Serial No</Label>
          <Input value={form.serial_no} onChange={(e)=>setForm({...form, serial_no:e.target.value})} />
        </div>
        <div className="space-y-2">
          <Label>Location</Label>
          <Input value={form.location} onChange={(e)=>setForm({...form, location:e.target.value})} />
        </div>
        <div className="space-y-2">
          <Label>Purchase Date</Label>
          <Input type="date" value={form.purchase_date} onChange={(e)=>setForm({...form, purchase_date:e.target.value})} />
        </div>
        <div className="space-y-2">
          <Label>Warranty End</Label>
          <Input type="date" value={form.warranty_end} onChange={(e)=>setForm({...form, warranty_end:e.target.value})} />
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e)=>setForm({...form, description:e.target.value})}
          data-testid={`${testPrefix}-description`} />
      </div>
    </>
  );
}

function ConditionPicker({ value, onChange }) {
  return (
    <div className="space-y-2">
      <Label>Condition (optional)</Label>
      <Select value={value || ""} onValueChange={(v)=>onChange(v==="none"?"":v)}>
        <SelectTrigger data-testid="lc-condition"><SelectValue placeholder="Keep current" /></SelectTrigger>
        <SelectContent>
          <SelectItem value="none">Keep current</SelectItem>
          {CONDITIONS.map((c) => <SelectItem key={c.v} value={c.v}>{c.l}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function LifecycleButton({ action, onClick }) {
  const map = {
    issue:    { l: "Issue",    Icon: LogOut,          className: "" },
    transfer: { l: "Transfer", Icon: ArrowRightLeft,  className: "" },
    return:   { l: "Return",   Icon: LogIn,           variant: "outline" },
    maintenance:       { l: "Send to Maintenance", Icon: Wrench, variant: "outline" },
    close_maintenance: { l: "Close Maintenance",   Icon: Check,  variant: "secondary" },
    retire:   { l: "Retire",   Icon: Recycle,         variant: "outline" },
    scrap:    { l: "Scrap",    Icon: Recycle,         variant: "destructive" },
  };
  const cfg = map[action] || { l: action, Icon: Sparkles };
  const Icon = cfg.Icon;
  return (
    <Button size="sm" variant={cfg.variant || "default"} onClick={onClick}
      data-testid={`lc-btn-${action}`}>
      <Icon className="w-3.5 h-3.5 mr-1.5" /> {cfg.l}
    </Button>
  );
}

function LifecycleBadge({ action }) {
  const cls = {
    commissioned: "bg-primary/10 text-primary",
    issue: "bg-blue-100 text-blue-800",
    return: "bg-emerald-100 text-emerald-800",
    transfer: "bg-indigo-100 text-indigo-800",
    maintenance: "bg-amber-100 text-amber-800",
    close_maintenance: "bg-emerald-100 text-emerald-800",
    retire: "bg-slate-200 text-slate-700",
    scrap: "bg-rose-100 text-rose-800",
  }[action] || "bg-secondary";
  return <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${cls}`}>
    {action.replace("_", " ")}
  </span>;
}

const Info = ({ label, value }) => (
  <div>
    <div className="eyebrow text-muted-foreground mb-1">{label}</div>
    <div className="font-medium">{value}</div>
  </div>
);
